<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { AddressPrecision } from '@roadfootage/api-client'
import type { Address, AddressDto } from '@roadfootage/api-client'

type AddressLike = Address | AddressDto

const props = defineProps<{
  modelValue: AddressLike | null | undefined
  rules?: any[]
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: AddressLike | null): void
  (e: 'location-selected', coords: { lat: number; lon: number }): void
  (e: 'location-cleared'): void
}>()

const selectRef = ref()
const addressOptions = ref<any[]>([])
// Text in the box, tracked so the clear button shows for typed-but-unpicked text too.
const inputText = ref<string>(props.modelValue?.rawInput || '')
const canClear = computed(() => inputText.value.length > 0 || !!addressSearchTerm.value)
const addressSearchTerm = ref<string | any>(props.modelValue?.rawInput || '')

// Keep the parent's DTO in sync as the user types (even if they don't select an option)
watch(addressSearchTerm, (newVal) => {
  if (typeof newVal === 'string') {
    emit('update:modelValue', {
      rawInput: newVal,
      formattedAddress: props.modelValue?.formattedAddress || null,
      precision: props.modelValue?.precision || AddressPrecision.Approximate
    } as AddressDto)
  }
})

// OSM Nominatim API Search
async function onAddressSearch(
  val: string,
  update: (callback: () => void) => void,
  abort: () => void
) {
  if (val.length < 3) {
    abort()
    return
  }

  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(val)}&countrycodes=au&addressdetails=1&limit=5`
    )
    const data = await response.json()

    update(() => {
      addressOptions.value = data.map((item: any) => ({
        label: item.display_name,
        value: item,
        lat: parseFloat(item.lat),
        lon: parseFloat(item.lon)
      }))
    })
  } catch (error) {
    console.error('Geocoding failed:', error)
    abort()
  }
}

// When the user clicks an explicit suggestion
function onAddressSelected(selectedOption: any) {
  if (!selectedOption) return

  // 1. Update the DTO safely
  emit('update:modelValue', {
    rawInput: addressSearchTerm.value.label || addressSearchTerm.value,
    formattedAddress: selectedOption.label,
    precision: AddressPrecision.Approximate
  } as AddressDto)

  // 2. Blast the coordinates up to the parent so they can update the map
  emit('location-selected', { lat: selectedOption.lat, lon: selectedOption.lon })

  // Make the input display just the string label, not the raw object
  addressSearchTerm.value = selectedOption.label
}

function onClear() {
  emit('update:modelValue', null)
  emit('location-cleared')
}

// Our own clear button instead of q-select's `clearable`: Quasar renders that icon aria-hidden but focusable,
// which fails axe (aria-hidden-focus). Behaves like Quasar's: empties the box and the value, then `clear`.
function clearAddress() {
  addressSearchTerm.value = null
  selectRef.value?.updateInputValue('')
  onClear()
}
</script>

<template>
  <q-select
    ref="selectRef"
    v-model="addressSearchTerm"
    :options="addressOptions"
    :rules="props.rules"
    outlined
    use-input
    hide-selected
    fill-input
    input-debounce="500"
    label="Enter address, intersection, road, or suburb..."
    label-color="blue-grey-9"
    color="blue-8"
    @filter="onAddressSearch"
    @update:model-value="onAddressSelected"
    @input-value="(value: string) => (inputText = value)"
  >
    <template v-slot:prepend>
      <q-icon name="mdi-map-marker" color="blue-grey-5" />
    </template>

    <template v-slot:append>
      <q-btn
        v-if="canClear"
        flat
        round
        dense
        size="sm"
        icon="mdi-close"
        aria-label="Clear address"
        @click.stop="clearAddress"
      />
    </template>

    <template v-slot:option="scope">
      <q-item v-bind="scope.itemProps">
        <q-item-section avatar>
          <q-icon name="mdi-map-marker-outline" color="blue-grey-4" />
        </q-item-section>
        <q-item-section>
          <q-item-label class="ellipsis">{{ scope.opt.label }}</q-item-label>
        </q-item-section>
      </q-item>
    </template>
  </q-select>
</template>
